import { useCallback, useMemo, useRef } from "react";

import type { Layout } from "react-grid-layout";
import { useContainerWidth } from "react-grid-layout";

import type { WidgetKind } from "../shared/widgetKind";
import { withDeskEvaluationGridSettings } from "../shared/workspace/deskEvaluationHydration";
import {
  applyFixedWidthPlacements,
  applyKindMinSizes,
  createDefaultPlacement,
  fromRglLayout,
  toRglLayout,
} from "../shared/workspace/placementAdapters";
import { useLayoutDocumentPersistence } from "../shared/workspace/useLayoutDocumentPersistence";
import {
  useWorkspace,
  useWorkspacePersist,
  useWorkspacePersistId,
  useWorkspaceStore,
} from "../shared/workspace/Workspace.context";
import { useOptionalWorkspaceRepository } from "../shared/workspace/WorkspaceRepository.context";
import {
  isGroupEligibleKind,
  resolveWorkspaceGroupId,
} from "../shared/workspace/workspaceGroup";
import {
  type ReactGridLayoutSettingsControllerValue,
  useReactGridLayoutSettingsController,
} from "./settings/ReactGridLayoutSettings.controller";
import {
  type AppliedReactGridLayoutProps,
  buildReactGridLayoutProps,
} from "./settings/reactGridLayoutSettings.adapter";
import { hydrateReactGridLayoutSettings } from "./settings/reactGridLayoutSettings.hydrate";

export { RGL_COLS } from "../shared/workspace/placementAdapters";

export interface ReactGridLayoutTabControllerValue {
  width: number;
  containerRef: ReturnType<typeof useContainerWidth>["containerRef"];
  mounted: boolean;
  layout: Layout;
  onAddWidget: (kind: WidgetKind) => void;
  onLayoutChange: (nextLayout: Layout) => void;
  settings: ReactGridLayoutSettingsControllerValue;
  appliedSettings: AppliedReactGridLayoutProps;
}

export const useReactGridLayoutTabController =
  (): ReactGridLayoutTabControllerValue => {
    const { width, containerRef, mounted } = useContainerWidth({
      measureBeforeMount: true,
    });

    const store = useWorkspaceStore();
    const persist = useWorkspacePersist();
    const workspaceId = useWorkspacePersistId();
    const repository = useOptionalWorkspaceRepository();

    const workspaceEntry =
      persist && workspaceId
        ? repository?.document.workspaces.find(
            (entry) => entry.id === workspaceId,
          )
        : undefined;
    const initialConfig = useRef(
      persist && workspaceId
        ? hydrateReactGridLayoutSettings(
            withDeskEvaluationGridSettings(
              workspaceEntry?.settings ?? {},
              workspaceEntry?.templateId,
            ),
          )
        : undefined,
    ).current;
    const settings = useReactGridLayoutSettingsController({ initialConfig });
    const appliedSettings = useMemo(
      () => buildReactGridLayoutProps(settings.config),
      [settings.config],
    );

    const hydrateSettings = useCallback(
      (remoteSettings: Record<string, unknown>) => {
        const entry = repository?.document.workspaces.find(
          (workspace) => workspace.id === workspaceId,
        );
        settings.hydrate(
          hydrateReactGridLayoutSettings(
            withDeskEvaluationGridSettings(remoteSettings, entry?.templateId),
          ),
        );
      },
      [repository, settings.hydrate, workspaceId],
    );

    useLayoutDocumentPersistence({
      enabled: persist,
      workspaceId,
      store,
      settings: { ...settings.config },
      onHydrateSettings: hydrateSettings,
    });

    const placements = useWorkspace((s) => s.placements);
    const widgets = useWorkspace((s) => s.widgets);
    const rawPlacements = useMemo(
      () => Object.values(placements),
      [placements],
    );
    const layout = useMemo((): Layout => {
      const kindByWidgetId = Object.fromEntries(
        Object.values(widgets).map((widget) => [widget.id, widget.kind]),
      );
      const containerPaddingX =
        settings.config.containerPaddingMode === "custom"
          ? settings.config.containerPaddingX
          : settings.config.marginX;
      const sizedPlacements = applyFixedWidthPlacements(
        applyKindMinSizes(rawPlacements, kindByWidgetId),
        {
          containerWidth: width,
          cols: settings.config.cols,
          marginX: settings.config.marginX,
          containerPaddingX,
        },
      );
      return toRglLayout(sizedPlacements);
    }, [rawPlacements, widgets, width, settings.config]);

    const onAddWidget = useCallback(
      (kind: WidgetKind) => {
        const id = crypto.randomUUID();
        const state = store.getState();
        const existingPlacements = Object.values(state.placements);
        const placement = createDefaultPlacement(id, kind, existingPlacements);
        // Resolved before the insert: the new widget is ungrouped, so it would
        // break the consensus it is about to join.
        const workspaceGroupId = resolveWorkspaceGroupId(state.widgets);
        state.addWidget({ id, kind, placement });
        if (workspaceGroupId !== null && isGroupEligibleKind(kind)) {
          store.getState().assignWidgetGroup(id, workspaceGroupId);
        }
      },
      [store],
    );

    const onLayoutChange = useCallback(
      (nextLayout: Layout) => {
        const previousPlacements = store.getState().placements;
        store.getState().setPlacements(
          fromRglLayout(nextLayout).map((placement) => {
            const previous = previousPlacements[placement.widgetId];
            const fixedWidthPx = previous?.fixedWidthPx;
            const minWidthPx = previous?.minWidthPx;
            const alignX = previous?.alignX;
            if (
              fixedWidthPx === undefined &&
              minWidthPx === undefined &&
              alignX === undefined
            ) {
              return placement;
            }
            return {
              ...placement,
              ...(fixedWidthPx === undefined ? {} : { fixedWidthPx }),
              ...(minWidthPx === undefined ? {} : { minWidthPx }),
              ...(alignX === undefined ? {} : { alignX }),
            };
          }),
        );
      },
      [store],
    );

    return {
      width,
      containerRef,
      mounted,
      layout,
      onAddWidget,
      onLayoutChange,
      settings,
      appliedSettings,
    };
  };
